/**
 * Signing in, and setting a password from the link EstateLark sends. Both stand outside the
 * app shell: nothing of a workspace shows until someone is signed in.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError, api } from "@/api/client";
import type { SessionInfo, TwoStepChallenge } from "@/api/models";
import { errorMessage, usePlans } from "@/api/queries";
import { type Chosen, choose, countiesWords, priceWords } from "@/lib/plans";
import { isChallenge, safeNext, startSession } from "@/lib/session";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PASSWORD_MIN = 12;

/** The landing page's neighborhood, so signing in reads as the same product. It is a picture
 * only, hidden from a screen reader, and stays daylight in both themes. */
function SignInPicture() {
  return (
    <div aria-hidden className="relative hidden overflow-hidden bg-(--bone) lg:block">
      <img src="/art/neighborhood.svg" alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute start-1/2 top-1/2 flex w-72 -translate-x-1/2 -translate-y-1/2 flex-col gap-3">
        <div className="overflow-hidden rounded-md border border-(--ink) bg-(--white) text-(--ink)">
          <p className="m-0 border-b border-(--ink) bg-highlighter px-4 py-2 text-sm">Court approved 5 days ago</p>
          <div className="flex flex-col gap-1 px-4 py-3">
            <p className="m-0 text-3xl leading-tight tracking-tight">$341,600</p>
            <p className="m-0 text-xs text-(--ink-soft)">1207 Example Rd, Houston 77000</p>
          </div>
        </div>
        <img src="/art/lot-approved.svg" alt="" className="mx-auto w-28" />
      </div>
      <div className="absolute inset-x-8 bottom-6 flex flex-wrap items-end justify-between gap-2">
        <p className="m-0 max-w-sm rounded-sm bg-(--white) px-3 py-2 text-(--ink)">Know when to call, not just who.</p>
        <p className="m-0 rounded-sm bg-(--white) px-3 py-2 text-xs text-(--ink)">Picture only. The estates are invented.</p>
      </div>
    </div>
  );
}

/** The page around a form that stands outside the app: the logo, the form, and the picture. */
export function SignInFrame({ title, subtitle, children }: { title: string; subtitle: React.ReactNode; children: React.ReactNode }) {
  return (
    <main className="grid min-h-dvh bg-surface-raised text-foreground lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <a href="https://estatelark.com" aria-label="EstateLark home" className="w-fit text-heading">
          <Logo className="h-9 w-auto" />
        </a>
        <div className="my-auto flex w-full max-w-[24rem] flex-col gap-6 self-center py-10">
          <div className="flex flex-col gap-2">
            <h1 id="sign-in-title" className="m-0 font-display text-3xl leading-tight text-heading">
              {title}
            </h1>
            <p className="m-0 text-base text-muted">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
      <SignInPicture />
    </main>
  );
}

const textLink = "text-link underline underline-offset-2";

export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" id="form-error" className="m-0 rounded-sm border border-danger bg-danger-tint px-3 py-2 text-sm text-danger-ink">
      {errorMessage(error)}
    </p>
  );
}

/** The second step of signing in: the code from the authenticator app, or a recovery code. */
function TwoStepCode({ challenge, onDone, onRestart }: { challenge: TwoStepChallenge; onDone: (session: SessionInfo) => void; onRestart: () => void }) {
  const [recovery, setRecovery] = React.useState(false);
  const [code, setCode] = React.useState("");
  const pass = useMutation({ mutationFn: () => api.passTwoStep(challenge.ticket, code), onSuccess: onDone });
  // A challenge that ended (too many wrong codes, or too slow) cannot be tried again.
  const ended = pass.error instanceof ApiError && pass.error.status === 401 && /expired/.test(pass.error.message);
  return (
    <SignInFrame
      title="Enter your code"
      subtitle={recovery ? "Type one of the recovery codes you saved. Each one works once." : "Open your authenticator app and type the 6-digit code for EstateLark."}
    >
      <form
        className="flex flex-col gap-4"
        id="two-step-form"
        onSubmit={(event) => {
          event.preventDefault();
          pass.mutate();
        }}
      >
        <FormError error={pass.error} />
        <div className="flex flex-col gap-2">
          <Label htmlFor="two-step-code">{recovery ? "Recovery code" : "6-digit code"}</Label>
          <Input
            id="two-step-code"
            key={recovery ? "recovery" : "code"}
            required
            autoFocus
            autoComplete="one-time-code"
            inputMode={recovery ? "text" : "numeric"}
            pattern={recovery ? undefined : "[0-9 ]{6,7}"}
            maxLength={recovery ? 20 : 7}
            spellCheck={false}
            autoCapitalize="off"
            className={recovery ? "font-mono" : "font-mono text-lg tracking-[0.3em]"}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>
        {ended ? (
          <Button type="button" id="two-step-restart" onClick={onRestart}>
            Sign in again
          </Button>
        ) : (
          <Button type="submit" id="two-step-btn" disabled={pass.isPending}>
            {pass.isPending ? "Checking" : "Continue"}
          </Button>
        )}
      </form>
      <button
        type="button"
        id="two-step-switch"
        className={`${textLink} w-fit cursor-pointer border-0 bg-transparent p-0 text-sm`}
        onClick={() => {
          setRecovery((value) => !value);
          setCode("");
          pass.reset();
        }}
      >
        {recovery ? "Use your authenticator app instead" : "Lost your phone? Use a recovery code"}
      </button>
    </SignInFrame>
  );
}

export function SignInPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const navigate = useNavigate();
  const client = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [challenge, setChallenge] = React.useState<TwoStepChallenge | null>(null);
  const finish = (session: SessionInfo) => {
    startSession(client, session);
    void navigate(next, { replace: true });
  };
  const signIn = useMutation({
    mutationFn: () => api.signIn(email, password),
    onSuccess: (answer) => {
      if (isChallenge(answer)) {
        setPassword("");
        setChallenge(answer);
      } else {
        finish(answer);
      }
    },
  });
  if (challenge) {
    return (
      <TwoStepCode
        challenge={challenge}
        onDone={finish}
        onRestart={() => {
          setChallenge(null);
          signIn.reset();
        }}
      />
    );
  }
  return (
    <SignInFrame title="Sign in" subtitle="Your probate leads, and when to call each family.">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          signIn.mutate();
        }}
      >
        <FormError error={signIn.error} />
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(event) => setEmail(event.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <Button type="submit" id="sign-in-btn" disabled={signIn.isPending}>
          {signIn.isPending ? "Signing in" : "Sign in"}
        </Button>
      </form>
      <div className="flex flex-col gap-2 text-sm text-muted">
        <Link to="/forgot-password" className={`${textLink} w-fit`}>
          Forgot your password?
        </Link>
        <p className="m-0">
          New to EstateLark?{" "}
          <Link to="/sign-up" className={textLink}>
            Create an account
          </Link>
        </p>
      </div>
    </SignInFrame>
  );
}

/** A new password, typed once: Show lets the agent read it back, and a password manager fills it. */
export function NewPassword({ id, value, onChange, label = "Password" }: { id: string; value: string; onChange: (value: string) => void; label?: string }) {
  const [shown, setShown] = React.useState(false);
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={shown ? "text" : "password"}
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN}
          autoFocus
          spellCheck={false}
          autoCapitalize="off"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="pe-20"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute end-1 top-1/2 -translate-y-1/2 gap-1.5 text-muted"
          aria-pressed={shown}
          aria-controls={id}
          onClick={() => setShown((value) => !value)}
          id={`${id}-show`}
        >
          {shown ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
          {shown ? "Hide" : "Show"}
        </Button>
      </div>
    </div>
  );
}

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const client = useQueryClient();
  const [password, setPassword] = React.useState("");
  const [challenge, setChallenge] = React.useState<TwoStepChallenge | null>(null);
  const finish = (session: SessionInfo) => {
    startSession(client, session);
    void navigate("/today", { replace: true });
  };
  const save = useMutation({
    mutationFn: () => api.setPassword(token, password),
    // The password is saved either way; an account with two-step sign-in still gives its code.
    onSuccess: (answer) => (isChallenge(answer) ? setChallenge(answer) : finish(answer)),
  });
  if (challenge) {
    return <TwoStepCode challenge={challenge} onDone={finish} onRestart={() => void navigate("/sign-in", { replace: true })} />;
  }
  if (!token) {
    return (
      <SignInFrame title="This link is not complete" subtitle="Open the whole link EstateLark sent you, or ask for a new one.">
        <Link to="/forgot-password" className={`${textLink} w-fit`}>
          Send me a new link
        </Link>
      </SignInFrame>
    );
  }
  return (
    <SignInFrame title="Choose your password" subtitle={`Use at least ${PASSWORD_MIN} characters. A short sentence is easy to remember.`}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <FormError error={save.error} />
        <NewPassword id="new-password" value={password} onChange={setPassword} />
        <Button type="submit" id="set-password-btn" disabled={save.isPending}>
          {save.isPending ? "Saving" : "Save password"}
        </Button>
      </form>
      {save.isError ? (
        <Link to="/forgot-password" className={`${textLink} w-fit text-sm`}>
          Send me a new link
        </Link>
      ) : null}
    </SignInFrame>
  );
}

/** One form for both ways in: an email goes out, and the next page says only that. */
function EmailLinkPage({
  title,
  subtitle,
  action,
  sentTitle,
  sentLine,
  submit,
  above,
  below,
}: {
  title: string;
  subtitle: string;
  action: string;
  sentTitle: string;
  sentLine: (email: string) => string;
  submit: (email: string, website: string) => Promise<unknown>;
  /** Shown above the email field, such as the county the pricing page sent. */
  above?: React.ReactNode;
  /** Shown under the button. */
  below?: React.ReactNode;
}) {
  const [email, setEmail] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const ask = useMutation({ mutationFn: (sent: { email: string; website: string }) => submit(sent.email, sent.website) });
  if (ask.isSuccess) {
    // The address the link went to, not whatever the box holds now.
    return (
      <SignInFrame title={sentTitle} subtitle={sentLine(ask.variables.email.trim())}>
        <p className="m-0 text-sm text-muted">The link works once, for one hour. Nothing yet? Check your spam folder.</p>
        <Button
          variant="outline"
          onClick={() => {
            ask.reset();
            setEmail("");
          }}
        >
          Use a different email
        </Button>
        <Link to="/sign-in" className={`${textLink} w-fit text-sm`}>
          Back to sign in
        </Link>
      </SignInFrame>
    );
  }
  return (
    <SignInFrame title={title} subtitle={subtitle}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          ask.mutate({ email, website });
        }}
      >
        <FormError error={ask.error} />
        {above}
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required autoFocus value={email} onChange={(event) => setEmail(event.target.value)} />
        </div>
        {/* Left empty by people. A bot that fills it is sent nothing. */}
        <div aria-hidden className="absolute -start-[9999px] size-px overflow-hidden">
          <label htmlFor="website">Leave this empty</label>
          <input id="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
        </div>
        <Button type="submit" disabled={ask.isPending}>
          {action}
        </Button>
        {below}
      </form>
      <p className="m-0 text-sm text-muted">
        Already have an account?{" "}
        <Link to="/sign-in" className={textLink}>
          Sign in
        </Link>
      </p>
    </SignInFrame>
  );
}

/** The counties the pricing page sent and their price, so the agent sees what they chose before they type. */
function ChosenPlan({ chosen }: { chosen: Chosen }) {
  return (
    <div id="chosen-plan" className="flex items-start justify-between gap-4 rounded-md border border-border bg-surface-soft px-4 py-3 text-sm">
      <p className="m-0 flex flex-col gap-0.5">
        <span className="font-semibold text-heading">{countiesWords(chosen.counties)}</span>
        <span className="text-muted">
          {chosen.plan.name}, {priceWords(chosen.plan, chosen.billing)}
          {chosen.counties.some((county) => county.opening_soon) ? ". Opening soon" : ""}
        </span>
      </p>
      <a href="https://estatelark.com/pricing.html" className={`${textLink} flex-none`}>
        Change
      </a>
    </div>
  );
}

export function SignUpPage() {
  const [params] = useSearchParams();
  const plans = usePlans();
  // The pricing page sends each county as its own `county`; the agent's own comes first.
  const chosen = choose(plans.data, params.getAll("county"), params.get("billing"));
  return (
    <EmailLinkPage
      title="Create your account"
      subtitle="Start with your email. We send a link, and you choose your password from there."
      action="Email me a link"
      sentTitle="Check your email"
      sentLine={(email) => `We sent a link to ${email}. Open it to choose your password.`}
      submit={(email, website) =>
        api.signUp(email, website, chosen ? { counties: chosen.counties.map((county) => county.slug), billing: chosen.billing } : {})
      }
      above={chosen ? <ChosenPlan chosen={chosen} /> : null}
      below={<p className="m-0 text-sm text-muted">Free to create. No card needed.</p>}
    />
  );
}

export function ForgotPasswordPage() {
  return (
    <EmailLinkPage
      title="Reset your password"
      subtitle="Enter your email, and we send a link to choose a new one."
      action="Email me a link"
      sentTitle="Check your email"
      sentLine={(email) => `If ${email} has an account, a link is on its way.`}
      submit={api.requestPasswordReset}
    />
  );
}
